import { useState, memo } from "react";
import classNames from "classnames";
import { useSetAtom } from "jotai";
import produce from "immer";
import _ from "underscore";
import { Col, Collapse, Row } from "reactstrap";
import IconButton from "elements/IconButton";
import Constants from "utils/Constants";
import ComplexMappedHeader, { ChevronTitle } from "./ComplexMappedHeader";
import MappedHeader from "./MappedHeader";
import { mappedHeadersAtom } from "./ImportDataWidget";
import FieldUtils from "utils/FieldUtils";

const complexFieldMap = Constants.COMPLEX_FIELDS;

const ComplexHeaderGroup = ({
  headerItem,
  index,
  field,
  availableActions,
  subitemFieldMap, // Provided for when the field is a subitem
  doubleNestedParent,
  doubleNestedIndex,
  fileHeaders,
  selectedHeaders,
}) => {
  const setMappedHeaders = useSetAtom(mappedHeadersAtom);
  const [open, setOpen] = useState(false);
  const isSingleSubitem = field?.FieldType === "Subitem";
  const isPartiallyComplex = FieldUtils.isPartiallyComplex(field?.FieldType);
  const title = `${field?.Name} ${
    isSingleSubitem || isPartiallyComplex ? "" : index + 1
  }`;

  return (
    <>
      <div className="border bg-superlight d-flex justify-content-between align-items-center pr-2 font-weight-500 cursor-pointer">
        <ChevronTitle
          title={title}
          open={open}
          css="text-dark"
          onClick={() => setOpen((prev) => !prev)}
        />
        {!isSingleSubitem && (
          <IconButton
            onClick={deleteGroup}
            icon="close"
            color="danger"
            tooltipText="Delete Group"
          />
        )}
      </div>
      <Collapse isOpen={open}>
        <Row className="align-items-center px-2 font-weight-600 h5 py-2 mb-0">
          <Col xs="6" sm="5">
            {field?.Name} Property
          </Col>
          <Col xs="6" sm="7">
            Mapped To
          </Col>
        </Row>
        {Object.keys(headerItem.Fields).map((APIName) => {
          let f;

          if (subitemFieldMap) {
            f = subitemFieldMap[`${field?.APIName}.${APIName}`];
          } else {
            f = _.findWhere(complexFieldMap[field?.FieldType], {
              APIName: APIName,
            });
          }

          if (!f) {
            return null;
          }

          if (
            subitemFieldMap &&
            (FieldUtils.isComplex(f?.FieldType) ||
              FieldUtils.isPartiallyComplex(f?.FieldType))
          ) {
            return (
              <ComplexMappedHeader
                key={f?.APIName}
                mappedHeader={headerItem.Fields[APIName]}
                availableActions={availableActions}
                field={f}
                doubleNestedParent={field?.APIName}
                doubleNestedIndex={index}
                fileHeaders={fileHeaders}
                selectedHeaders={selectedHeaders}
              />
            );
          }

          return (
            <Row
              className={classNames("align-items-center px-2 mb-2")}
              key={f?.APIName}
            >
              <Col xs="6" sm="5">
                {f?.Name}
              </Col>
              <Col xs="6" sm="7">
                <MappedHeader
                  mappedHeader={headerItem.Fields[f?.APIName]}
                  field={field}
                  availableActions={availableActions}
                  parentIndex={index}
                  nestedField={f}
                  useRecordCreateUpdateOptions
                  fieldType={f?.FieldType}
                  doubleNestedParent={doubleNestedParent}
                  doubleNestedIndex={doubleNestedIndex}
                  fileHeaders={fileHeaders}
                  selectedHeaders={selectedHeaders}
                />
              </Col>
            </Row>
          );
        })}
      </Collapse>
    </>
  );

  function handleDeleteGroup() {
    setMappedHeaders(
      produce((newHeaders) => {
        if (doubleNestedParent) {
          newHeaders[doubleNestedParent].Fields[doubleNestedIndex].Fields[
            field?.APIName
          ].Fields.splice(index, 1);
        } else {
          newHeaders[field?.APIName].Fields.splice(index, 1);
        }
      })
    );
  }

  function deleteGroup(e) {
    e.stopPropagation();

    handleDeleteGroup(index);
  }
};

export default memo(ComplexHeaderGroup);
